import type {
  Contact_Update_Destroyable,
  Job_Read,
  Job_Update,
  JobAssignment_Read,
  JobAssignment_Read_Nested,
} from '@treadinc/horizon-api-spec';
import {
  Company_Read_Nested,
  Driver_Read_Nested,
  GeofenceStrategy,
  GpsStatus,
  Impersonation_Read_Minimal,
  Job_Bulk_Update,
  Job_Invoicing_Read,
  Job_Read_Minimal,
  Job_Read_Nested,
  JobCompanyDetail_Read,
  JobETA_Read_Nested,
  JobETAState,
  JobEvent_Read,
  JobEventName,
  JobState,
  JobStateEvent,
  JobSummary_Read,
  M2MClient_Read_Nested,
  OrderUnitOfMeasure,
  Principal_Read_Nested,
  PrincipalType,
  RateType,
  User_Read,
  User_Read_Minimal,
  Waypoint_Create,
  Waypoint_Update_Destroyable,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { BulkEditJobDTO } from '~components/Job/BulkEditJobDialog';
import { ACTIVE_JOB_STATES } from '~components/Maps/v2/constants';
import { ContactTypes } from '~constants/enums';
import { AccountBasic } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { BasicEquipment, EquipmentTypeBasic } from '~hooks/useEquipment/models';
import { InvoiceBasic } from '~hooks/useInvoices/models';
import { Load } from '~hooks/useLoad/models';
import { MaterialRateAssignment } from '~hooks/useMaterialRates/models';
import { BasicMaterial, Material } from '~hooks/useMaterials/models';
import { OrderBasic } from '~hooks/useOrders/models';
import {
  contactTypeMap,
  ExtendedContactItem,
  filterContact,
  Phase,
  ProjectBasic,
  transformContact,
} from '~hooks/useProjects/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { BasicServiceClass } from '~hooks/useServiceClass/models';
import { Service } from '~hooks/useServices/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { WayPoint, WayPointFormProps } from '~hooks/useSites/models';
import { User, UserBasic } from '~hooks/useUsers/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import {
  deparseUpdateRateDetailsDTO,
  RateDetailsDTO,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

import { ApprovalStatus } from '../useApprovalsAssignments/models';
import { DeliveredQuantity } from '../useOrders/models';
export interface JobFormProps {
  id: string;
  jobStartAt: Nullable<Dayjs>;
  notes: string | null;
  quantity: number;
  unitOfMeasure: ItemNameAndId;
  service: Service;
  material: ItemNameAndId | Material;
  equipment: ItemNameAndId;
  additionalEquipment?: ItemNameAndId;
  pickUpWayPoint: Nullable<WayPoint>;
  dropOffWayPoint: Nullable<WayPoint>;
  stagingWayPoint: Nullable<WayPoint>;
  salesContact: ExtendedContactItem;
  foremanContact: ExtendedContactItem;
  supervisorContact: ExtendedContactItem;
  collaboratorContact: ExtendedContactItem;
  priority?: number;
  phase?: Phase | null;
  allContacts?: JobContactCandidate[];
  vendorAccount?: AccountBasic;
  loads: Array<Load>;
  deletedLoadIds: string[];
  foremen: Array<User>;
  jobNotes: string;
  orderNotes: string;
  projectNotes: string;
  internalNotes: string;
  vendor: { label: string; value: string } | null;
  driver: { label: string; value: string } | null;
  requestedEquipmentTypeName?: string | null;
  requestedLoadsCount: Nullable<number>;
  equipmentTypeGrossCapacity: Nullable<number>;
  equipmentTypeGrossCapacityUnitOfMeasure: ItemNameAndId | null;
  recurringLoads: boolean;
  haulerRateType: { label: string; value: RateType } | null;
  collaborators: Array<UserBasic>;
  loadUnitOfMeasure: ItemNameAndId | null;
  jobTime: number | null;
  salespeople: Array<UserBasic>;
  siteRateSchedule?: SiteRateSchedule | null;
}

export interface JobFormPropsWithRates extends JobFormProps {
  vendorPayCommissionPercentage?: Job_Update['vendor_pay_commission_percentage'];
  driverPayCommissionPercentage?: Job_Update['driver_pay_commission_percentage'];
  customerRate?: RateDetailsDTO['targetRate'];
  customerRateType?: RateDetailsDTO['targetRateType'];
  customerRateValue?: RateDetailsDTO['targetRateValue'];
  assigneeRate?: {
    target: 'vendor' | 'driver';
    rate: RateDetailsDTO['targetRate'];
    rateType: RateDetailsDTO['targetRateType'];
    rateValue: RateDetailsDTO['targetRateValue'];
  };
  customerAddOnAssignments: AddOnAssignmentSchema[];
  vendorAddOnAssignments: AddOnAssignmentSchema[];
  driverAddOnAssignments: AddOnAssignmentSchema[];
  customerJobAssignment: Nullable<JobAssignmentNested>;
  vendorJobAssignment: Nullable<JobAssignmentNested>;
  siteRateSchedule?: SiteRateSchedule | null;
}

type JobContactPayload = Contact_Update_Destroyable | null;

interface JobContacts {
  foremanContact: JobContactPayload;
  salesContact: JobContactPayload;
  collaboratorContact: JobContactPayload;
  supervisorContact: JobContactPayload;
}

interface JobContactCandidate extends Contact_Update_Destroyable {
  type?: Nullable<ItemNameAndId>;
  isDeleted?: boolean;
}

interface JobContactsSource {
  allContacts?: JobContactCandidate[] | null;
}

export class JobMinimal {
  constructor(
    private _id: string,
    private _jobId: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get jobId() {
    return this._jobId;
  }

  public static parse(proto: Job_Read_Minimal) {
    return new JobMinimal(proto.id, proto.job_id);
  }
}

export class JobTripEvent {
  constructor(
    private _id: string | undefined,
    private _lat: string,
    private _lon: string,
    private _state: string | JobState,
    private _event_name: JobEventName,
    private _created_by: Nullable<Principal_Read_Nested>,
    private _created_at: Dayjs,
    private _occurred_at: Dayjs,
    private _load_id: string | null,
    private _payload: {
      reject_reason?: string | null;
      label?: string | null;
      driver_id?: string | null;
      vendor_id?: string | null;
      from_value?: string | null;
      to_value?: string | null;
      value?: string | null;
    } | null,
  ) {}

  public static parse(proto: JobEvent_Read) {
    return new JobTripEvent(
      proto.id,
      proto.lat ?? '',
      proto.lon ?? '',
      proto.state,
      proto.event_name,
      proto.created_by ?? null,
      dayjs(proto.created_at),
      dayjs(proto.occurred_at) ?? dayjs(),
      proto.load_id ?? null,
      proto.payload ?? null,
    );
  }

  public get id() {
    return this._id;
  }

  public get lat() {
    return parseFloat(this._lat);
  }

  public get lng() {
    return parseFloat(this._lon);
  }

  public get state() {
    return this._state;
  }

  public get eventName() {
    return this._event_name;
  }

  public get createdBy() {
    return this._created_by;
  }

  public get payload() {
    return this._payload;
  }

  public get createdByName() {
    switch (this.createdBy?.type) {
      case PrincipalType.COMPANY: {
        const company = this.createdBy as Company_Read_Nested;

        return company.legal_name;
      }

      case PrincipalType.USER: {
        const user = this.createdBy as User_Read_Minimal;

        return user.first_name === 'Automation'
          ? 'System'
          : `${user.first_name} ${user.last_name}`;
      }

      case PrincipalType.M2M_CLIENT: {
        const client = this.createdBy as M2MClient_Read_Nested;

        return client.company.legal_name;
      }

      case PrincipalType.IMPERSONATION: {
        const impersonation = this.createdBy as Impersonation_Read_Minimal;

        return `${impersonation.impersonator.first_name} ${impersonation.impersonator.last_name} impersonating ${impersonation.impersonated.first_name} ${impersonation.impersonated.last_name}`;
      }

      default:
        return '';
    }
  }

  public get createdAt() {
    return this._created_at;
  }
  public get occurredAt() {
    return this._occurred_at;
  }

  public get loadId() {
    return this._load_id;
  }
}

class JobCompanyDetail {
  public static parse(proto: JobCompanyDetail_Read) {
    return new JobCompanyDetail(proto.internal_notes ?? null);
  }

  public get internalNotes() {
    return this._internal_notes;
  }

  constructor(private _internal_notes: string | null) {}
}

/**
 * BE returns the SiteRateSchedule stamp in per-party slots:
 *   - Job.driver_site_rate_schedule (driver)
 *   - customer_job_assignment.site_rate_schedule (customer)
 *   - vendor_job_assignment.site_rate_schedule (vendor)
 *
 * JobService::Update fans a single incoming `site_rate_schedule_id` out to
 * all three columns, so the sides should agree for jobs written through the
 * form. Coalesce in customer → vendor → driver order so the FE surfaces the
 * stamp regardless of which side was populated first.
 */
function parseFirstSchedule(proto: Job_Read): Nullable<SiteRateSchedule> {
  const customerSchedule = proto.customer_job_assignment?.site_rate_schedule;
  if (customerSchedule) return SiteRateSchedule.parse(customerSchedule);

  const vendorSchedule = proto.vendor_job_assignment?.site_rate_schedule;
  if (vendorSchedule) return SiteRateSchedule.parse(vendorSchedule);

  const driverSchedule = proto.driver_site_rate_schedule;
  if (driverSchedule) return SiteRateSchedule.parse(driverSchedule);

  return null;
}

export type JobRateEditable = Job_Read['rate_editable'];

export class Job {
  public static parse(proto: Job_Read): Job {
    return new Job(
      proto.id ?? '',
      proto.editable,
      proto.rate_editable,
      proto.state ?? '',
      proto.state_changed_at ? dayjs(proto.state_changed_at) : null,
      dayjs(proto.job_start_at),
      proto.notes ?? '',
      proto.quantity ?? '',
      proto.unit_of_measure
        ? ItemNameAndId.parse({ name: proto.unit_of_measure, id: proto.unit_of_measure })
        : null,
      proto.job_id ?? '',
      OrderBasic.parse(proto.order),
      proto.project ? ProjectBasic.parse(proto.project) : null,
      proto.phase ? Phase.parse(proto.phase) : null,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto.additional_equipment?.length
        ? BasicEquipment.parse(proto.additional_equipment[0])
        : null,
      proto.requested_equipment_type_name,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto.service ? Service.parse(proto.service) : null,
      proto.waypoints.map(WayPoint.parse),
      proto.waypoints
        .filter((point) => {
          return point.type === WaypointType.PICKUP;
        })
        .map(WayPoint.parse),
      proto.waypoints
        .filter((point) => {
          return point.type === WaypointType.DROP_OFF;
        })
        .map(WayPoint.parse),
      proto.waypoints
        .filter((point) => {
          return point.type === WaypointType.STAGING;
        })
        .map(WayPoint.parse),
      proto.company ? CompanyBasic.parse(proto.company) : null,
      proto.state_events,
      proto.approvable,
      proto.approved,
      proto.unapprovable,
      proto.deletable,
      proto.gps_status || null,
      proto.sales_contact
        ? ExtendedContactItem.parse({ ...proto.sales_contact, type: ContactTypes.SALES })
        : null,
      proto.foreman_contact
        ? ExtendedContactItem.parse({
            ...proto.foreman_contact,
            type: ContactTypes.FOREMAN,
          })
        : null,
      proto.collaborator_contact
        ? ExtendedContactItem.parse({
            ...proto.collaborator_contact,
            type: ContactTypes.COLLABORATOR,
          })
        : null,
      proto.supervisor_contact
        ? ExtendedContactItem.parse({
            ...proto.supervisor_contact,
            type: ContactTypes.SUPERVISOR,
          })
        : null,
      proto.driver ? DriverBasic.parse(proto.driver as Driver_Read_Nested) : null,
      proto.priority ?? 0,
      proto.billable_hours ?? '',
      proto.created_at ? dayjs(proto.created_at) : dayjs(),
      proto.updated_at ? dayjs(proto.updated_at) : dayjs(),
      proto.load_cycle_avg ?? 0,
      proto.customer_job_assignment
        ? JobAssignmentNested.parse(proto.customer_job_assignment)
        : null,
      proto.vendor_job_assignment
        ? JobAssignmentNested.parse(proto.vendor_job_assignment)
        : null,
      proto.foremen?.length
        ? proto?.foremen.map((item) => User.parse(item as User_Read))
        : [],
      proto.hauler_rate_type ? (proto.hauler_rate_type as RateType) : null,
      proto.driver_rate ? RateBasicWithValue.parse(proto.driver_rate) : null,
      isNil(proto.driver_rate_value) ? null : Number(proto.driver_rate_value),
      proto.driver_rate_type ?? null,
      proto.loads_count,
      proto.completed_loads_count,
      proto.tickets_count,
      proto.copyable,
      proto.splittable,
      proto.requested_loads_count,
      proto.equipment_type_gross_capacity,
      proto.equipment_type_gross_capacity_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.equipment_type_gross_capacity_unit_of_measure,
            id: proto.equipment_type_gross_capacity_unit_of_measure,
          })
        : null,
      proto.job_summary ? JobSummary.parse(proto.job_summary) : null,
      proto.recurring_loads,
      proto.approval_status ? ApprovalStatus.parse(proto.approval_status) : undefined,
      (proto.collaborators ?? []).map((user) => UserBasic.parse(user)),
      proto.paused_at ? dayjs(proto.paused_at) : undefined,
      proto.load_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.load_unit_of_measure,
            id: proto.load_unit_of_measure,
          })
        : null,
      proto.job_time ?? undefined,
      proto.approved_at ? dayjs(proto.approved_at) : null,
      (proto.salespeople ?? []).map((user) => UserBasic.parse(user)),
      proto.billing_approved_at ? dayjs(proto.billing_approved_at) : null,
      proto.field_approved_at ? dayjs(proto.field_approved_at) : null,
      proto.service_class ? BasicServiceClass.parse(proto.service_class) : null,
      proto.geofence_delay_seconds,
      proto.geofence_strategy,
      proto.job_company_detail ? JobCompanyDetail.parse(proto.job_company_detail) : null,
      proto.driver_or_vendor_assignable,
      proto.veta_center_bound,
      proto.material_rate_assignment
        ? MaterialRateAssignment.parse(proto.material_rate_assignment)
        : null,
      // BE now returns per-party stamps: driver stamp on Job, customer/vendor
      // stamps nested on each assignment. JobService::Update fans a single
      // incoming site_rate_schedule_id out to all three, so any populated side
      // represents the same schedule. Coalesce in party order so the UI sees
      // the stamp regardless of which side writes populated it.
      parseFirstSchedule(proto),
      proto.driver_site_rate_schedule
        ? SiteRateSchedule.parse(proto.driver_site_rate_schedule)
        : null,
      proto.live_eta ?? false,
      proto.job_eta ? JobEtaBasic.parse(proto.job_eta) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.messaging_enabled,
      proto.driver_pay_commission_percentage ?? null,
      proto.driver_commission_resolution ?? null,
      proto.net_driver_rate ?? null,
      proto.driver_commission_locked ?? null,
      proto.inherited_driver_pay_commission_percentage ?? null,
    );
  }

  public get driverPayCommissionPercentage() {
    return this._driver_pay_commission_percentage;
  }
  public get driverCommissionResolution() {
    return this._driver_commission_resolution;
  }
  public get driverCommissionLocked() {
    return this._driver_commission_locked;
  }
  public get inheritedDriverPayCommissionPercentage() {
    return this._inherited_driver_pay_commission_percentage;
  }
  public get netDriverRate() {
    return this._net_driver_rate;
  }

  public static deparseBulkUpdate(ids: string[], job: BulkEditJobDTO): Job_Bulk_Update {
    const pickUpWaypoint: Nullable<Waypoint_Create> = (job.pickUpWayPoint as WayPoint)
      ?.site?.id
      ? WayPoint.deparse(job.pickUpWayPoint as WayPointFormProps, true)
      : null;
    const dropOffWaypoint: Nullable<Waypoint_Create> = (job.dropOffWayPoint as WayPoint)
      ?.site?.id
      ? WayPoint.deparse(job.dropOffWayPoint as WayPointFormProps, true)
      : null;
    const stagingWaypoint: Nullable<Waypoint_Create> = (job.stagingWayPoint as WayPoint)
      ?.site?.id
      ? WayPoint.deparse(job.stagingWayPoint as WayPointFormProps, true)
      : null;

    const waypoints: Array<Waypoint_Create> = [
      pickUpWaypoint,
      dropOffWaypoint,
      stagingWaypoint,
    ].filter((waypoint): waypoint is Waypoint_Create => waypoint !== null);

    const res: Job_Bulk_Update = {
      job_ids: ids,
      data: {
        notes: job.notes || undefined,
        ...(job.jobStartAt ? { job_start_at: dayjs(job.jobStartAt)?.toISOString() } : {}),
        ...(job.equipmentType?.name
          ? { requested_equipment_type_name: job.equipmentType.name }
          : {}),
        ...(job.material?.id ? { material_id: job.material?.id } : {}),
        ...(job.equipmentType?.name
          ? { equipment_type_gross_capacity: job.equipmentTypeGrossCapacity ?? null }
          : {}),
        ...(job.equipmentTypeGrossCapacityUnitOfMeasure?.id
          ? {
              equipment_type_gross_capacity_unit_of_measure:
                job.equipmentTypeGrossCapacityUnitOfMeasure?.id,
            }
          : {}),
        ...(job.foremen?.length
          ? { foreman_ids: job.foremen?.map((foreman) => foreman.id) }
          : {}),
        ...(waypoints?.length ? { waypoints: waypoints } : {}),
        ...(job.jobUnitOfMeasure?.id
          ? { unit_of_measure: job.jobUnitOfMeasure?.id as OrderUnitOfMeasure }
          : {}),
        ...(job.jobQuantity ? { quantity: job.jobQuantity } : {}),
        ...(job.recurringLoads !== null && { recurring_loads: job.recurringLoads }),
      },
    };

    return res;
  }

  public static deparseUpdate(job: JobFormPropsWithRates): Job_Update {
    const contacts = this.getContacts(job);

    const hasCustomerRateInput =
      job.customerRate !== undefined ||
      job.customerRateType !== undefined ||
      job.customerRateValue !== undefined;
    const customerRate = deparseUpdateRateDetailsDTO(
      'customer',
      hasCustomerRateInput
        ? {
            targetRate: job.customerRate ?? null,
            targetRateType: job.customerRateType ?? null,
            targetRateValue: job.customerRateValue ?? null,
          }
        : undefined,
    );
    const vendorOrDriverRate = job.assigneeRate
      ? deparseUpdateRateDetailsDTO(job.assigneeRate.target, {
          targetRate: job.assigneeRate.rate,
          targetRateType: job.assigneeRate.rateType,
          targetRateValue: job.assigneeRate.rateValue,
        })
      : {};

    const pickUpWaypointId = job.pickUpWayPoint?.id;
    const dropOffWaypointId = job.dropOffWayPoint?.id;
    const stagingWaypointId = job.stagingWayPoint?.id;

    const pickUpWayPoint = (job.pickUpWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(job.pickUpWayPoint as WayPointFormProps)
      : null;
    const dropOffWayPoint = (job.dropOffWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(job.dropOffWayPoint as WayPointFormProps)
      : null;
    const stagingWayPoint = (job.stagingWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(job.stagingWayPoint as WayPointFormProps)
      : null;

    const waypoints: Array<Waypoint_Update_Destroyable> | undefined = [
      ...(pickUpWayPoint
        ? [pickUpWayPoint]
        : pickUpWaypointId
          ? [{ id: pickUpWaypointId, _destroy: 1 }]
          : []),
      ...(dropOffWayPoint
        ? [dropOffWayPoint]
        : dropOffWaypointId
          ? [{ id: dropOffWaypointId, _destroy: 1 }]
          : []),
      ...(stagingWayPoint
        ? [stagingWayPoint]
        : stagingWaypointId
          ? [{ id: stagingWaypointId, _destroy: 1 }]
          : []),
    ];

    const scheduleStamps: { site_rate_schedule_id?: string | null } = {};
    const scheduleId =
      job.siteRateSchedule?.id ?? (job.siteRateSchedule === null ? null : undefined);

    if (scheduleId !== undefined) {
      scheduleStamps.site_rate_schedule_id = scheduleId;
    }

    // The truck type only travels when the caller actually carries a value for
    // it. Sending it on every save let a form whose state had not yet caught up
    // with the loaded job clear the job's truck type without the user ever
    // touching that field.
    const requestedEquipmentType: Pick<Job_Update, 'requested_equipment_type_name'> = {};
    if (job.requestedEquipmentTypeName !== undefined) {
      requestedEquipmentType.requested_equipment_type_name =
        job.requestedEquipmentTypeName;
    }

    const parsedJobTime = parseFiniteNumber(job?.jobTime);

    const res: Job_Update & { id: string } = {
      id: job.id,
      job_start_at: job.jobStartAt ? dayjs(job.jobStartAt)?.toISOString() : undefined,
      notes: job.jobNotes,
      quantity:
        isNil(job.quantity) || !String(job.quantity).length ? null : Number(job.quantity),
      unit_of_measure: (job?.unitOfMeasure?.id as OrderUnitOfMeasure) ?? null,
      equipment_id: job.equipment?.id ?? null,
      additional_equipment_ids: job.additionalEquipment
        ? [job.additionalEquipment.id]
        : [],
      material_id: job.material?.id ?? null,
      waypoints,
      priority: job.priority || 0,
      phase_id: job.phase === null ? null : job.phase?.id,
      foreman_ids: (job.foremen || []).map((foreman) => foreman.id) || [],
      collaborator_ids: (job.collaborators || []).map((collaborator: UserBasic) => {
        return String(collaborator.id);
      }),
      sales_contact: contacts.salesContact ?? undefined,
      foreman_contact: contacts.foremanContact ?? undefined,
      collaborator_contact: contacts.collaboratorContact ?? undefined,
      supervisor_contact: contacts.supervisorContact ?? undefined,
      ...customerRate,
      ...(job.vendorPayCommissionPercentage !== undefined
        ? { vendor_pay_commission_percentage: job.vendorPayCommissionPercentage }
        : {}),
      ...(job.driverPayCommissionPercentage !== undefined
        ? { driver_pay_commission_percentage: job.driverPayCommissionPercentage }
        : {}),
      ...vendorOrDriverRate,
      equipment_type_gross_capacity:
        isNil(job.equipmentTypeGrossCapacity) ||
        `${job.equipmentTypeGrossCapacity}` === ''
          ? null
          : job.equipmentTypeGrossCapacity,
      equipment_type_gross_capacity_unit_of_measure: job
        .equipmentTypeGrossCapacityUnitOfMeasure?.id
        ? (job.equipmentTypeGrossCapacityUnitOfMeasure.id as OrderUnitOfMeasure)
        : null,
      recurring_loads: Boolean(job.recurringLoads),
      hauler_rate_type: job.haulerRateType?.value ?? null,
      load_unit_of_measure: (job?.loadUnitOfMeasure?.id as OrderUnitOfMeasure) ?? null,
      job_time: parsedJobTime === null ? null : parsedJobTime * 60,
      salesperson_ids: (job.salespeople || []).map((salesperson: UserBasic) => {
        return String(salesperson.id);
      }),
      internal_notes: job.internalNotes || null,
      ...requestedEquipmentType,
      ...scheduleStamps,
    };

    return res;
  }

  // The job parameter should use some type reference so that we know what properties are available (see: TRE-2938)
  public static deparseUpdateFromInlineMode(job: Job) {
    const scheduledDate = job.jobStartAt
      ? dayjs(job.jobStartAt)
      : dayjs(job._job_start_at);

    const scheduledTime = job.jobStartAt
      ? dayjs(job.jobStartAt)
      : dayjs(job._job_start_at);

    const combinedDateTime = scheduledDate
      .hour(scheduledTime.hour())
      .minute(scheduledTime.minute())
      .second(scheduledTime.second());
    const quantity = job.quantity || job._quantity;

    const result: Job_Update & { id: string } = {
      id: job._id,
      job_start_at: combinedDateTime.isValid()
        ? combinedDateTime.toISOString()
        : undefined,
      priority: job.priority || job._priority,
      quantity: isNil(quantity) || !String(quantity).length ? null : Number(quantity),
      unit_of_measure: (job._unit_of_measure?.id as OrderUnitOfMeasure) ?? null,
      equipment_id: job._equipment?.id || null,
      additional_equipment_ids: job._additional_equipment?.id
        ? [job._additional_equipment.id]
        : [],
      material_id: job._material?.id,
      driver_id: job._driver?.id,
    };

    return result;
  }

  private static getContacts(job: JobContactsSource): JobContacts {
    const contacts: JobContacts = {
      foremanContact: null,
      salesContact: null,
      collaboratorContact: null,
      supervisorContact: null,
    };

    (Object.keys(contactTypeMap) as Array<keyof JobContacts>).forEach((key) => {
      const contactType = contactTypeMap[key];
      const contact = filterContact(job, contactType);
      contacts[key] = transformContact(contact, true) as JobContactPayload;
    });

    return contacts;
  }

  public get id(): string {
    return this._id;
  }

  public get editable(): boolean {
    return this._editable;
  }

  public get vetaCenterBound() {
    return this._veta_center_bound;
  }

  public get rateEditable() {
    return this._rate_editable;
  }

  public get status(): JobState {
    return this._state;
  }

  public get statusChangedAt(): dayjs.Dayjs | null {
    return this._state_changed_at;
  }
  public get isActive(): boolean {
    return ACTIVE_JOB_STATES.includes(this._state);
  }
  public get isFlagged(): boolean {
    return this._state === JobState.FLAGGED;
  }
  public get isCompleted(): boolean {
    return this._state === JobState.COMPLETED;
  }
  public get isPayed(): boolean {
    return this._state === JobState.PAID;
  }
  public get isSignedOff(): boolean {
    return this._state === JobState.SIGNED_OFF;
  }
  public get jobStartAt() {
    return this._job_start_at;
  }

  public get notes(): string | null {
    return this._notes;
  }

  public get quantity(): string {
    return this._quantity;
  }

  public set quantity(value: string) {
    this._quantity = value;
  }

  public get unitOfMeasure(): Nullable<ItemNameAndId> {
    return this._unit_of_measure;
  }

  public get driverFullName(): string {
    return [this._driver?.firstName, this._driver?.lastName]
      .filter((item) => !!item)
      .join(' ');
  }

  public get jobId(): string {
    return this._job_id;
  }

  public get equipment(): Nullable<BasicEquipment> {
    return this._equipment;
  }
  public get additionalEquipment(): Nullable<BasicEquipment> {
    return this._additional_equipment;
  }
  public get requestedEquipmentTypeName(): Nullable<string> {
    return this._requested_equipment_type_name;
  }
  public get material(): Nullable<BasicMaterial> {
    return this._material;
  }
  public get service(): Nullable<Service> {
    return this._service;
  }

  public get waypoints(): WayPoint[] {
    return this._waypoints;
  }

  public get pickUpWayPoints(): WayPoint[] {
    return this._pickUpWayPoints;
  }
  public get dropOffWayPoints(): WayPoint[] {
    return this._dropOffWayPoints;
  }
  public get stagingWayPoints(): WayPoint[] {
    return this._stagingWayPoints;
  }

  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }

  public get project(): Nullable<ProjectBasic> {
    return this._project;
  }
  public get projectName(): string {
    // Fake
    return this._project?.name || '';
  }

  public get phase(): Nullable<Phase> {
    return this._phase;
  }

  public get order() {
    return this._order;
  }

  public get orderId() {
    return this._order.orderId;
  }

  public get isSalesContact(): boolean {
    return Boolean(this._sales_contact?.id.length);
  }

  public get isSupervisorContact(): boolean {
    return Boolean(this._supervisor_contact?.id.length);
  }

  public get isForemanContact(): boolean {
    return Boolean(this._foreman_contact?.id.length);
  }

  public get isCollaboratorContact(): boolean {
    return Boolean(this._collaborator_contact?.id.length);
  }

  public get foremanContact(): Nullable<ExtendedContactItem> {
    return this._foreman_contact;
  }

  public get salesContact(): Nullable<ExtendedContactItem> {
    return this._sales_contact;
  }

  public get supervisorContact(): Nullable<ExtendedContactItem> {
    return this._supervisor_contact;
  }

  public get collaboratorContact(): Nullable<ExtendedContactItem> {
    return this._collaborator_contact;
  }

  public get allContacts(): ExtendedContactItem[] {
    const allContacts = [];
    if (this._sales_contact) {
      allContacts.push(this._sales_contact);
    }
    if (this._foreman_contact) {
      allContacts.push(this._foreman_contact);
    }
    if (this._collaborator_contact) {
      allContacts.push(this._collaborator_contact);
    }
    if (this._supervisor_contact) {
      allContacts.push(this._supervisor_contact);
    }
    return allContacts;
  }

  public get createdAt(): Dayjs {
    return this._createdAt || dayjs();
  }
  public get updatedAt(): Dayjs {
    return this._updatedAt || dayjs();
  }

  public get driver(): Nullable<DriverBasic> {
    return this._driver;
  }

  public get priority(): number {
    return this._priority || 0;
  }

  public get billableHours(): string {
    return this._billable_hours || '';
  }

  public get loadCycleAvg(): number {
    return this._load_cycle_avg || 0;
  }

  public get customerJobAssignment(): Nullable<JobAssignmentNested> {
    return this._customer_job_assignment;
  }

  public get vendorJobAssignment(): Nullable<JobAssignmentNested> {
    return this._vendor_job_assignment;
  }

  public get stateEvents(): JobStateEvent[] {
    return this._state_events;
  }

  public get foremen(): Array<User> {
    return this._foremen || [];
  }

  public get collaborators() {
    return this._collaborators;
  }

  public get haulerRateType(): Nullable<RateType> {
    return this._hauler_rate_type;
  }
  public get jobNotes(): Nullable<string> {
    return this.notes;
  }

  public get internalNotes() {
    return this._job_company_detail?.internalNotes ?? null;
  }

  public get driverRate() {
    return this._driver_rate;
  }

  public get driverRateValue() {
    return this._driver_rate_value;
  }

  public get driverRateType() {
    return this._driver_rate_type;
  }

  public get loadsCount() {
    return this._loads_count;
  }

  public get completedLoadsCount() {
    return this._completed_loads_count;
  }

  public get ticketsCount() {
    return this._tickets_count;
  }

  public get copyable() {
    return Boolean(this._copyable);
  }

  public get splittable() {
    return Boolean(this._splittable);
  }

  public get approvable() {
    return Boolean(this._approvable);
  }

  public get approved() {
    return Boolean(this._approved);
  }

  public get unapprovable() {
    return Boolean(this._unapprovable);
  }

  public get deletable() {
    return Boolean(this._deletable);
  }

  public get requestedLoadsCount() {
    return this._requested_loads_count;
  }

  public set requestedLoadsCount(value: Nullable<number>) {
    this._requested_loads_count = value;
  }

  public get equipmentTypeGrossCapacity() {
    return this._equipment_type_gross_capacity;
  }

  public set equipmentTypeGrossCapacity(value: Nullable<string>) {
    this._equipment_type_gross_capacity = value;
  }

  public get jobSummary() {
    return this._job_summary;
  }

  public set jobSummary(value: Nullable<JobSummary>) {
    this._job_summary = value;
  }

  public get recurringLoads() {
    return this._recurring_loads;
  }

  public get approvalStatus() {
    return this._approval_status;
  }

  public get pausedAt() {
    return this._paused_at;
  }
  public get gpsStatus(): Nullable<GpsStatus> {
    return this._gps_status;
  }

  public get loadUnitOfMeasure() {
    return this._load_unit_of_measure;
  }

  public get equipmentTypeGrossCapacityUnitOfMeasure() {
    return this._equipment_type_gross_capacity_unit_of_measure;
  }

  public get jobTime() {
    return this._job_time;
  }

  public get approvedAt(): Nullable<Dayjs> {
    return this._approved_at;
  }

  public get salespeople() {
    return this._salespeople || [];
  }

  public get billingApprovedAt() {
    return this._billing_approved_at;
  }

  public get fieldApprovedAt() {
    return this._field_approved_at;
  }

  public get serviceClass() {
    return this._service_class ?? null;
  }
  public set serviceClass(value: Nullable<BasicServiceClass>) {
    this._service_class = value;
  }

  public get geofenceDelaySeconds() {
    return this._geofence_delay_seconds;
  }

  public get geofenceStrategy() {
    return this._geofence_strategy;
  }

  public get jobCompanyDetail() {
    return this._job_company_detail ?? null;
  }

  public get materialRateAssignment() {
    return this._material_rate_assignment ?? null;
  }

  public get siteRateSchedule(): Nullable<SiteRateSchedule> {
    return this._site_rate_schedule ?? null;
  }

  public get siteRateScheduleId(): Nullable<string> {
    return this._site_rate_schedule?.id ?? null;
  }

  /**
   * The driver's own rate-card stamp, kept separate from the coalesced
   * `siteRateSchedule` so provenance can be attributed to the driver specifically.
   */
  public get driverSiteRateSchedule() {
    return this._driver_site_rate_schedule ?? null;
  }

  public get liveEta() {
    return this._live_eta;
  }

  /**
   * The live dispatch ETA record — distinct from `liveEta`, the opt-in boolean. Present
   * only while the backend tracks the job: opted in, and between enroute and terminal.
   */
  public get eta() {
    return this._job_eta;
  }

  /** Backend-computed: may the company being acted as dispatch this job at all? */
  public get driverOrVendorAssignable() {
    return Boolean(this._driver_or_vendor_assignable);
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get messagingEnabled() {
    return this._messaging_enabled;
  }

  constructor(
    private _id: string,
    private _editable: boolean,
    private _rate_editable: JobRateEditable,
    private _state: JobState,
    private _state_changed_at: dayjs.Dayjs | null,
    private _job_start_at: Dayjs,
    private _notes: string | null,
    private _quantity: string,
    private _unit_of_measure: Nullable<ItemNameAndId>,
    private _job_id: string,
    private _order: OrderBasic,
    private _project: Nullable<ProjectBasic>,
    private _phase: Nullable<Phase>,
    private _equipment: Nullable<BasicEquipment>,
    private _additional_equipment: Nullable<BasicEquipment>,
    private _requested_equipment_type_name: Nullable<string>,
    private _material: Nullable<BasicMaterial>,
    private _service: Nullable<Service>,
    private _waypoints: WayPoint[],
    private _pickUpWayPoints: WayPoint[],
    private _dropOffWayPoints: WayPoint[],
    private _stagingWayPoints: WayPoint[],
    private _company: Nullable<CompanyBasic>,
    private _state_events: JobStateEvent[],
    private _approvable: boolean,
    private _approved: boolean,
    private _unapprovable: boolean,
    private _deletable: boolean = false,
    private _gps_status: Nullable<GpsStatus>,

    private _foreman_contact?: Nullable<ExtendedContactItem>,
    private _sales_contact?: Nullable<ExtendedContactItem>,
    private _supervisor_contact?: Nullable<ExtendedContactItem>,
    private _collaborator_contact?: Nullable<ExtendedContactItem>,
    private _driver?: Nullable<DriverBasic>,
    private _priority?: number,
    private _billable_hours?: string,
    private _createdAt?: dayjs.Dayjs,
    private _updatedAt?: dayjs.Dayjs,
    private _load_cycle_avg?: number,
    private _customer_job_assignment?: Nullable<JobAssignmentNested>,
    private _vendor_job_assignment?: Nullable<JobAssignmentNested>,
    private _foremen?: Array<User> | null,
    private _hauler_rate_type?: Nullable<RateType>,
    private _driver_rate?: Nullable<RateBasicWithValue>,
    private _driver_rate_value?: Nullable<number>,
    private _driver_rate_type?: Nullable<RateType>,
    private _loads_count?: number,
    private _completed_loads_count?: number,
    private _tickets_count?: number,
    private _copyable?: boolean,
    private _splittable?: boolean,
    private _requested_loads_count?: Nullable<number>,
    private _equipment_type_gross_capacity?: string | null,
    private _equipment_type_gross_capacity_unit_of_measure?: ItemNameAndId | null,
    private _job_summary?: Nullable<JobSummary>,
    private _recurring_loads?: boolean,
    private _approval_status?: ApprovalStatus,
    private _collaborators?: Array<UserBasic>,
    private _paused_at?: dayjs.Dayjs,
    private _load_unit_of_measure?: ItemNameAndId | null,
    private _job_time?: number,
    private _approved_at?: Nullable<Dayjs>,
    private _salespeople?: Array<UserBasic>,
    private _billing_approved_at?: Nullable<Dayjs>,
    private _field_approved_at?: Nullable<Dayjs>,
    private _service_class?: Nullable<BasicServiceClass>,
    private _geofence_delay_seconds?: number,
    private _geofence_strategy?: GeofenceStrategy,
    private _job_company_detail?: Nullable<JobCompanyDetail>,
    private _driver_or_vendor_assignable?: boolean,
    private _veta_center_bound: boolean = false,
    private _material_rate_assignment?: Nullable<MaterialRateAssignment>,
    private _site_rate_schedule?: Nullable<SiteRateSchedule>,
    private _driver_site_rate_schedule?: Nullable<SiteRateSchedule>,
    // Dispatch Live ETA opt-in; read-only on Job — mirrors the parent order server-side
    // (TRE-15771).
    private _live_eta: boolean = false,
    private _job_eta: JobEtaBasic | null = null,
    private _discarded_at: Dayjs | null = null,
    private _messaging_enabled: boolean = false,
    private _driver_pay_commission_percentage: NonNullable<
      Job_Read['driver_pay_commission_percentage']
    > | null = null,
    private _driver_commission_resolution: NonNullable<
      Job_Read['driver_commission_resolution']
    > | null = null,
    private _net_driver_rate: NonNullable<Job_Read['net_driver_rate']> | null = null,
    private _driver_commission_locked: NonNullable<
      Job_Read['driver_commission_locked']
    > | null = null,
    private _inherited_driver_pay_commission_percentage: NonNullable<
      Job_Read['inherited_driver_pay_commission_percentage']
    > | null = null,
  ) {}
}

export class JobAssignmentNested {
  public static parse(proto: JobAssignment_Read_Nested) {
    return new JobAssignmentNested(
      proto.id ?? '',
      proto.customer_account ? AccountBasic.parse(proto.customer_account) : null,
      proto.vendor_account ? AccountBasic.parse(proto.vendor_account) : null,
      proto.rate ? RateBasicWithValue.parse(proto.rate) : null,
      isNil(proto.rate_value) ? null : Number(proto.rate_value),
      proto.rate_type ?? null,
      proto.site_rate_schedule ? SiteRateSchedule.parse(proto.site_rate_schedule) : null,
      proto.vendor_pay_commission_percentage ?? null,
      proto.vendor_commission_resolution ?? null,
      proto.net_vendor_rate ?? null,
      proto.vendor_commission_locked ?? null,
      proto.vendor_equipment_type
        ? EquipmentTypeBasic.parse(proto.vendor_equipment_type)
        : null,
      proto.inherited_vendor_pay_commission_percentage ?? null,
    );
  }

  public get vendorPayCommissionPercentage() {
    return this._vendor_pay_commission_percentage;
  }
  public get vendorCommissionResolution() {
    return this._vendor_commission_resolution;
  }
  public get vendorCommissionLocked() {
    return this._vendor_commission_locked;
  }
  public get inheritedVendorPayCommissionPercentage() {
    return this._inherited_vendor_pay_commission_percentage;
  }
  public get netVendorRate() {
    return this._net_vendor_rate;
  }

  public get id(): string {
    return this._id;
  }

  public get customerAccount(): Nullable<AccountBasic> {
    return this._customer_account;
  }

  public get vendorAccount(): Nullable<AccountBasic> {
    return this._vendor_account;
  }

  public get rate(): Nullable<RateBasicWithValue> {
    return this._rate;
  }

  public get rateValue() {
    return this._rate_value;
  }

  public get rateType() {
    return this._rate_type;
  }

  /**
   * The rate card this party's rate was stamped from. Read it per party rather than
   * through `Job#siteRateSchedule`, which coalesces the three sides (see
   * `parseFirstSchedule`) and so cannot attribute a card to a specific party.
   */
  public get siteRateSchedule() {
    return this._site_rate_schedule ?? null;
  }

  public get vendorEquipmentType() {
    return this._vendor_equipment_type ?? null;
  }

  constructor(
    private _id: string,
    private _customer_account?: Nullable<AccountBasic>,
    private _vendor_account?: Nullable<AccountBasic>,
    private _rate?: Nullable<RateBasicWithValue>,
    private _rate_value?: Nullable<number>,
    private _rate_type?: Nullable<RateType>,
    private _site_rate_schedule?: Nullable<SiteRateSchedule>,
    private _vendor_pay_commission_percentage: NonNullable<
      JobAssignment_Read_Nested['vendor_pay_commission_percentage']
    > | null = null,
    private _vendor_commission_resolution: NonNullable<
      JobAssignment_Read_Nested['vendor_commission_resolution']
    > | null = null,
    private _net_vendor_rate: NonNullable<
      JobAssignment_Read_Nested['net_vendor_rate']
    > | null = null,
    private _vendor_commission_locked: NonNullable<
      JobAssignment_Read_Nested['vendor_commission_locked']
    > | null = null,
    private _vendor_equipment_type?: Nullable<EquipmentTypeBasic>,
    private _inherited_vendor_pay_commission_percentage: NonNullable<
      JobAssignment_Read_Nested['inherited_vendor_pay_commission_percentage']
    > | null = null,
  ) {}
}
interface JobAssignmentJobNested {
  id: string;
}
export class JobAssignment extends JobAssignmentNested {
  public static parse(proto: JobAssignment_Read) {
    return new JobAssignment(
      proto.id ?? '',
      proto.customer_account ? AccountBasic.parse(proto.customer_account) : null,
      proto.vendor_account ? AccountBasic.parse(proto.vendor_account) : null,
      proto.rate ? RateBasicWithValue.parse(proto.rate) : null,
      proto.job as JobAssignmentJobNested,
      proto.created_at ? dayjs(proto.created_at) : dayjs(),
      proto.updated_at ? dayjs(proto.updated_at) : dayjs(),
      isNil(proto.rate_value) ? null : Number(proto.rate_value),
      proto.rate_type ?? null,
      proto.site_rate_schedule ? SiteRateSchedule.parse(proto.site_rate_schedule) : null,
      proto.vendor_pay_commission_percentage ?? null,
      proto.vendor_commission_resolution ?? null,
      proto.net_vendor_rate ?? null,
      proto.vendor_commission_locked ?? null,
    );
  }

  public get jobId(): string {
    return this._job.id;
  }

  public get createdAt(): Dayjs {
    return this._created_at || dayjs();
  }

  public get updatedAt(): Dayjs {
    return this._updated_at || dayjs();
  }

  constructor(
    id: string,
    customerAccount: Nullable<AccountBasic>,
    vendorAccount: Nullable<AccountBasic>,
    rate: Nullable<RateBasicWithValue>,
    private _job: JobAssignmentJobNested,
    private _created_at?: Dayjs,
    private _updated_at?: Dayjs,
    rateValue: number | null = null,
    rateType: RateType | null = null,
    siteRateSchedule: SiteRateSchedule | null = null,
    vendorPayCommissionPercentage: NonNullable<
      JobAssignment_Read['vendor_pay_commission_percentage']
    > | null = null,
    vendorCommissionResolution: NonNullable<
      JobAssignment_Read['vendor_commission_resolution']
    > | null = null,
    netVendorRate: NonNullable<JobAssignment_Read['net_vendor_rate']> | null = null,
    vendorCommissionLocked: NonNullable<
      JobAssignment_Read['vendor_commission_locked']
    > | null = null,
  ) {
    super(
      id,
      customerAccount,
      vendorAccount,
      rate,
      rateValue,
      rateType,
      siteRateSchedule,
      vendorPayCommissionPercentage,
      vendorCommissionResolution,
      netVendorRate,
      vendorCommissionLocked,
    );
  }
}

export class JobBasic {
  constructor(
    private _id: Nullable<string>,
    private _job_id: string,
    private _loads_count: number,
    private _job_start_at: Dayjs,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get jobId() {
    return this._job_id;
  }

  public get loadsCount() {
    return this._loads_count;
  }

  public get jobStartAt() {
    return this._job_start_at;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Job_Read_Nested) {
    return new JobBasic(
      proto.id,
      proto.job_id,
      proto.loads_count,
      dayjs(proto.job_start_at),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class JobEtaBasic {
  constructor(
    private _id: string,
    private _state: JobETAState,
    private _idle_at: Dayjs | null,
    private _eta_at: Dayjs | null,
  ) {}

  public static parse(proto: JobETA_Read_Nested) {
    return new JobEtaBasic(
      proto.id,
      proto.state,
      proto.idle_at ? dayjs(proto.idle_at) : null,
      proto.eta_at ? dayjs(proto.eta_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get state() {
    return this._state;
  }

  /** When the truck arrived on site, i.e. when the dwell started. */
  public get idleAt() {
    return this._idle_at;
  }

  public get etaAt() {
    return this._eta_at;
  }
}

export class JobSummary {
  constructor(
    private _logging_started_at: Dayjs | null,
    private _logging_ended_at: Dayjs | null,
    private _completed_loads_count: number,
    private _break_time_minutes: number,
    private _work_time_minutes: number,
    private _approved_work_time_minutes: Nullable<number>,
    private _first_geofence_entry_at: Nullable<Dayjs>,
    private _last_geofence_exit_at: Nullable<Dayjs>,
    private _delivered_quantities: Array<DeliveredQuantity>,
    private _job_phase_name: Nullable<string>,
    private _job_phase_code: Nullable<string>,
    private _foreman_external_id: Nullable<string>,
    private _editable: boolean,
    private _adjusted_logging_started_at: Dayjs | null,
    private _adjusted_logging_ended_at: Dayjs | null,
    private _loads_with_tickets_count: number,
    private _travelled_distance_meters: string | null,
    private _idle_time_seconds: number,
  ) {}

  public get loggingStartedAt() {
    return this._logging_started_at;
  }

  public get loggingEndedAt() {
    return this._logging_ended_at;
  }

  public get completedLoadsCount() {
    return this._completed_loads_count;
  }

  public get breakTimeMinutes() {
    return this._break_time_minutes;
  }

  public get workTimeMinutes() {
    return this._work_time_minutes;
  }

  public get approvedWorkTimeMinutes() {
    return this._approved_work_time_minutes;
  }

  public get firstGeofenceEntryAt() {
    return this._first_geofence_entry_at;
  }

  public get lastGeofenceExitAt() {
    return this._last_geofence_exit_at;
  }

  public get deliveredQuantities(): Array<DeliveredQuantity> {
    return this._delivered_quantities;
  }

  public get jobPhaseName() {
    return this._job_phase_name;
  }

  public get jobPhaseCode() {
    return this._job_phase_code;
  }

  public get foremanExternalId() {
    return this._foreman_external_id;
  }

  public get editable() {
    return this._editable;
  }

  public get adjustedLoggingStartedAt() {
    return this._adjusted_logging_started_at;
  }

  public get adjustedLoggingEndedAt() {
    return this._adjusted_logging_ended_at;
  }

  public get loadsWithTicketsCount() {
    return this._loads_with_tickets_count;
  }

  public get travelledDistanceMeters() {
    return this._travelled_distance_meters;
  }

  public get idleTimeSeconds() {
    return this._idle_time_seconds;
  }

  public static parse(proto: JobSummary_Read) {
    return new JobSummary(
      proto.logging_started_at ? dayjs(proto.logging_started_at) : null,
      proto.logging_ended_at ? dayjs(proto.logging_ended_at) : null,
      proto.completed_loads_count,
      proto.break_time_minutes,
      proto.work_time_minutes,
      proto.approved_work_time_minutes,
      proto.first_geofence_entry_at ? dayjs(proto.first_geofence_entry_at) : null,
      proto.last_geofence_exit_at ? dayjs(proto.last_geofence_exit_at) : null,
      proto.delivered_quantities.map(DeliveredQuantity.parse),
      proto.job_phase_name,
      proto.job_phase_code,
      proto.foreman_external_id,
      proto.editable,
      proto.adjusted_logging_started_at ? dayjs(proto.adjusted_logging_started_at) : null,
      proto.adjusted_logging_ended_at ? dayjs(proto.adjusted_logging_ended_at) : null,
      proto.loads_with_tickets_count,
      proto.travelled_distance_meters ?? null,
      proto.idle_time_seconds,
    );
  }
}

export class JobInvoicing extends Job {
  public static parse(proto: Job_Invoicing_Read): JobInvoicing {
    const job = Job.parse(proto);
    const newJobInvoicing = new JobInvoicing(
      job.id,
      job.editable,
      job.rateEditable,
      job.status,
      job.statusChangedAt,
      job.jobStartAt,
      job.notes,
      job.quantity,
      job.unitOfMeasure,
      job.jobId,
      job.order,
      job.project,
      job.phase,
      job.equipment,
      job.additionalEquipment,
      job.requestedEquipmentTypeName,
      job.material,
      job.service,
      job.waypoints,
      job.pickUpWayPoints,
      job.dropOffWayPoints,
      job.stagingWayPoints,
      job.company,
      job.stateEvents,
      job.approvable,
      job.approved,
      job.unapprovable,
      job.deletable,
      job.gpsStatus,
      job.foremanContact,
      job.salesContact,
      job.supervisorContact,
      job.collaboratorContact,
      job.driver,
      job.priority,
      job.billableHours,
      job.createdAt,
      job.updatedAt,
      job.loadCycleAvg,
      job.customerJobAssignment,
      job.vendorJobAssignment,
      job.foremen,
      job.haulerRateType,
      job.driverRate,
      job.driverRateValue,
      job.driverRateType,
      job.loadsCount,
      job.completedLoadsCount,
      job.ticketsCount,
      job.copyable,
      job.splittable,
      job.requestedLoadsCount,
      job.equipmentTypeGrossCapacity,
      job.equipmentTypeGrossCapacityUnitOfMeasure,
      job.jobSummary,
      job.recurringLoads,
      job.approvalStatus,
      job.collaborators,
      job.pausedAt,
      job.loadUnitOfMeasure,
      job.jobTime,
      job.approvedAt,
      job.salespeople,
      job.billingApprovedAt,
      job.fieldApprovedAt,
      job.serviceClass,
      job.geofenceDelaySeconds,
      job.geofenceStrategy,
      job.jobCompanyDetail,
      proto.receivable_invoice ? InvoiceBasic.parse(proto.receivable_invoice) : null,
      proto.payable_invoice ? InvoiceBasic.parse(proto.payable_invoice) : null,
      job.driverOrVendorAssignable,
      job.vetaCenterBound,
      job.materialRateAssignment,
      job.siteRateSchedule,
      job.driverSiteRateSchedule,
      job.liveEta,
      job.eta,
      job.discardedAt,
      job.messagingEnabled,
      job.driverPayCommissionPercentage,
      job.driverCommissionResolution,
      job.netDriverRate,
      job.driverCommissionLocked,
      job.inheritedDriverPayCommissionPercentage,
    );
    return newJobInvoicing;
  }

  public get receivableInvoice() {
    return this._receivable_invoice;
  }
  public set receivableInvoice(value: Nullable<InvoiceBasic>) {
    this._receivable_invoice = value;
  }

  public get payableInvoice() {
    return this._payable_invoice;
  }
  public set payableInvoice(value: Nullable<InvoiceBasic>) {
    this._payable_invoice = value;
  }

  public static fromJobOrJobInvoicingAndInvoices(
    job: Job | JobInvoicing,
    receivableInvoice: Nullable<InvoiceBasic>,
    payableInvoice: Nullable<InvoiceBasic>,
  ) {
    const jobInvoicing = job as JobInvoicing;
    jobInvoicing.receivableInvoice = receivableInvoice;
    jobInvoicing.payableInvoice = payableInvoice;

    return jobInvoicing;
  }

  constructor(
    _id: string,
    _editable: boolean,
    _rate_editable: JobRateEditable,
    _state: JobState,
    _state_changed_at: dayjs.Dayjs | null,
    _job_start_at: Dayjs,
    _notes: string | null,
    _quantity: string,
    _unit_of_measure: Nullable<ItemNameAndId>,
    _job_id: string,
    _order: OrderBasic,
    _project: Nullable<ProjectBasic>,
    _phase: Nullable<Phase>,
    _equipment: Nullable<BasicEquipment>,
    _additional_equipment: Nullable<BasicEquipment>,
    _requested_equipment_type_name: Nullable<string>,
    _material: Nullable<BasicMaterial>,
    _service: Nullable<Service>,
    _waypoints: WayPoint[],
    _pickUpWayPoints: WayPoint[],
    _dropOffWayPoints: WayPoint[],
    _stagingWayPoints: WayPoint[],
    _company: Nullable<CompanyBasic>,
    _state_events: JobStateEvent[],
    _approvable: boolean,
    _approved: boolean,
    _unapprovable: boolean,
    _deletable: boolean = false,
    _gps_status: Nullable<GpsStatus>,
    _foreman_contact?: Nullable<ExtendedContactItem>,
    _sales_contact?: Nullable<ExtendedContactItem>,
    _supervisor_contact?: Nullable<ExtendedContactItem>,
    _collaborator_contact?: Nullable<ExtendedContactItem>,
    _driver?: Nullable<DriverBasic>,
    _priority?: number,
    _billable_hours?: string,
    _createdAt?: dayjs.Dayjs,
    _updatedAt?: dayjs.Dayjs,
    _load_cycle_avg?: number,
    _customer_job_assignment?: Nullable<JobAssignmentNested>,
    _vendor_job_assignment?: Nullable<JobAssignmentNested>,
    _foremen?: Array<User> | null,
    _hauler_rate_type?: Nullable<RateType>,
    _driver_rate?: Nullable<RateBasicWithValue>,
    _driver_rate_value?: Nullable<number>,
    _driver_rate_type?: Nullable<RateType>,
    _loads_count?: number,
    _completed_loads_count?: number,
    _tickets_count?: number,
    _copyable?: boolean,
    _splittable?: boolean,
    _requested_loads_count?: Nullable<number>,
    _equipment_type_gross_capacity?: string | null,
    _equipment_type_gross_capacity_unit_of_measure?: ItemNameAndId | null,
    _job_summary?: Nullable<JobSummary>,
    _recurring_loads?: boolean,
    _approval_status?: ApprovalStatus,
    _collaborators?: Array<UserBasic>,
    _paused_at?: dayjs.Dayjs,
    _load_unit_of_measure?: ItemNameAndId | null,
    _job_time?: number,
    _approved_at?: Nullable<Dayjs>,
    _salespeople?: Array<UserBasic>,
    _billing_approved_at?: Nullable<Dayjs>,
    _field_approved_at?: Nullable<Dayjs>,
    _service_class?: Nullable<BasicServiceClass>,
    _geofence_delay_seconds?: number,
    _geofence_strategy?: GeofenceStrategy,
    _job_company_detail?: Nullable<JobCompanyDetail>,
    private _receivable_invoice?: Nullable<InvoiceBasic>,
    private _payable_invoice?: Nullable<InvoiceBasic>,
    _driver_or_vendor_assignable?: boolean,
    _veta_center_bound?: boolean,
    _material_rate_assignment: MaterialRateAssignment | null = null,
    _site_rate_schedule: SiteRateSchedule | null = null,
    _driver_site_rate_schedule: SiteRateSchedule | null = null,
    _live_eta: boolean = false,
    _job_eta: JobEtaBasic | null = null,
    _discarded_at: Dayjs | null = null,
    _messaging_enabled: boolean = false,
    _driver_pay_commission_percentage: NonNullable<
      Job_Read['driver_pay_commission_percentage']
    > | null = null,
    _driver_commission_resolution: NonNullable<
      Job_Read['driver_commission_resolution']
    > | null = null,
    _net_driver_rate: NonNullable<Job_Read['net_driver_rate']> | null = null,
    _driver_commission_locked: NonNullable<
      Job_Read['driver_commission_locked']
    > | null = null,
    _inherited_driver_pay_commission_percentage: NonNullable<
      Job_Read['inherited_driver_pay_commission_percentage']
    > | null = null,
  ) {
    super(
      _id,
      _editable,
      _rate_editable,
      _state,
      _state_changed_at,
      _job_start_at,
      _notes,
      _quantity,
      _unit_of_measure,
      _job_id,
      _order,
      _project,
      _phase,
      _equipment,
      _additional_equipment,
      _requested_equipment_type_name,
      _material,
      _service,
      _waypoints,
      _pickUpWayPoints,
      _dropOffWayPoints,
      _stagingWayPoints,
      _company,
      _state_events,
      _approvable,
      _approved,
      _unapprovable,
      _deletable,
      _gps_status,
      _foreman_contact,
      _sales_contact,
      _supervisor_contact,
      _collaborator_contact,
      _driver,
      _priority,
      _billable_hours,
      _createdAt,
      _updatedAt,
      _load_cycle_avg,
      _customer_job_assignment,
      _vendor_job_assignment,
      _foremen,
      _hauler_rate_type,
      _driver_rate,
      _driver_rate_value,
      _driver_rate_type,
      _loads_count,
      _completed_loads_count,
      _tickets_count,
      _copyable,
      _splittable,
      _requested_loads_count,
      _equipment_type_gross_capacity,
      _equipment_type_gross_capacity_unit_of_measure,
      _job_summary,
      _recurring_loads,
      _approval_status,
      _collaborators,
      _paused_at,
      _load_unit_of_measure,
      _job_time,
      _approved_at,
      _salespeople,
      _billing_approved_at,
      _field_approved_at,
      _service_class,
      _geofence_delay_seconds,
      _geofence_strategy,
      _job_company_detail,
      _driver_or_vendor_assignable,
      _veta_center_bound,
      _material_rate_assignment,
      _site_rate_schedule,
      _driver_site_rate_schedule,
      _live_eta,
      _job_eta,
      _discarded_at,
      _messaging_enabled,
      _driver_pay_commission_percentage,
      _driver_commission_resolution,
      _net_driver_rate,
      _driver_commission_locked,
      _inherited_driver_pay_commission_percentage,
    );
  }
}
